<main class="container">
    <section id="column1" npSetLayout [pageLayout]="PageLayout.Layout12">

        @if (isLoading) {
        <div class="text-center py-5 mt-4">
            <div class="mb-3" style="display:inline-block;"><mat-spinner [diameter]="40"></mat-spinner></div>
            <p class="text-muted">Loading your attestation...</p>
        </div>
        }

        @if (!isLoading && loadError) {
        <div class="alert alert-danger mt-4">{{ loadError }}</div>
        }

        @if (!isLoading && !loadError && batchContext) {

        <div class="mt-4 mb-1 text-center">
            <h2 class="fw-bold mb-0" style="color:#1a202c;">Tier 0 Permission Attestation</h2>
            <p class="text-muted mt-1 mb-0" style="font-size:.9rem;">Review each permission granted to <strong>{{ batchContext.servicePrincipalDisplayName }}</strong> and confirm it is genuinely still needed.</p>
        </div>
        <div class="mt-3 mb-3 px-3 py-2" style="background:#fef2f2;border:1px solid #fecaca;border-radius:6px;font-size:.85rem;color:#7f1d1d;">
            <strong>&#9888; These are Tier 0 permissions — the highest-risk access Chevron grants.</strong>
            Misuse or compromise of a Tier 0 permission can affect the entire company, not just this application. For each one, ask yourself <em>"do we still genuinely need this?"</em> — don't treat this as a routine checkbox.
        </div>
        <!-- Single panel: progress + collapsible guide — one border, no alignment split -->
        <div class="mt-3 mb-4" style="background:white;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
            <div class="d-flex justify-content-between align-items-center px-3 pt-3 pb-2" style="background:white;">
                <div class="text-muted" style="font-size:.82rem;">{{ batchContext.campaignId }}</div>
                <div class="text-end">
                    <div class="fw-bold lh-1" style="font-size:1.4rem;color:#0d6efd;">{{ completedCount }}<span class="text-muted fw-normal" style="font-size:.9rem;"> / {{ allPermissions.length }}</span></div>
                    <div class="text-muted" style="font-size:.75rem;">reviewed</div>
                </div>
            </div>
            <div class="px-3 pb-2" style="background:white;">
                <div class="progress" style="height:6px;border-radius:3px;background:#dee2e6;">
                    <div class="progress-bar bg-primary" style="transition:width .4s ease;"
                         [style.width.%]="allPermissions.length > 0 ? (completedCount / allPermissions.length) * 100 : 0">
                    </div>
                </div>
            </div>
            <!-- trigger is white-on-white with border separator; guide body is grey, matching site convention -->
            <div class="d-flex justify-content-between align-items-center px-3 py-2"
                 style="cursor:pointer;background:#f8f9fa;border-top:1px solid #e2e8f0;user-select:none;"
                 (click)="showGuide = !showGuide">
                <span style="font-size:.82rem;font-weight:600;color:#4f46e5;">
                    <span style="margin-right:.35rem;">&#9432;</span>How does this work?
                </span>
                <span style="font-size:.72rem;color:#6b7280;font-weight:500;">{{ showGuide ? 'Hide' : 'Show guide' }}</span>
            </div>
            @if (showGuide) {
            <div class="row g-0 text-start" style="border-top:1px solid #e2e8f0;font-size:.82rem;line-height:1.7;background:white;">
                <!-- Two attestation types explained -->
                <div class="col-12 p-3 d-flex gap-3" style="border-bottom:1px solid #e2e8f0;background:#f8f9fa;">
                    <div class="card flex-fill mb-0" style="border-color:#c7d2fe;">
                        <div class="card-header py-2 px-3" style="background:#eef2ff;border-color:#c7d2fe;font-size:.78rem;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:#4338ca;">
                            &#9679; Full Review
                        </div>
                        <div class="card-body p-3" style="font-size:.82rem;">
                            <p class="mb-2 text-muted">Shown when there is <strong>no previous attestation on record</strong> for this permission, or it was previously <strong>removed</strong> or <strong>escalated</strong> for manual review.</p>
                            <p class="mb-0 text-muted">You will be asked to write a business justification from scratch. The AI will evaluate it and either accept it or tell you what is missing. A decision cannot be recorded until the justification is evaluated.</p>
                        </div>
                    </div>
                    <div class="card flex-fill mb-0" style="border-color:#d1d5db;">
                        <div class="card-header py-2 px-3" style="background:#f9fafb;border-color:#d1d5db;font-size:.78rem;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:#374151;">
                            &#9679; Renewal
                        </div>
                        <div class="card-body p-3" style="font-size:.82rem;">
                            <p class="mb-2 text-muted">Shown when this permission was <strong>confirmed in the previous cycle</strong>. Your previous justification is displayed for reference.</p>
                            <p class="mb-0 text-muted"><strong>Read it carefully.</strong> Confirm it still accurately reflects why this permission is needed today — scope, usage, and business need unchanged. If anything has changed, or if you have any doubt the permission is still actively required, use <strong>Remove</strong> or <strong>Escalate</strong> rather than confirming.</p>
                        </div>
                    </div>
                </div>
                <div class="col-md-3 p-3" style="border-right:1px solid #d1d5db;">
                    <div class="fw-semibold mb-2" style="font-size:.75rem;text-transform:uppercase;letter-spacing:.05em;color:#64748b;">Steps</div>
                    <ol class="text-muted mb-0 ps-3">
                        <li class="mb-2">Read the permission name and description in the card below.</li>
                        <li class="mb-2">Write a justification explaining why this permission is needed and click <strong>Evaluate Justification</strong>. The AI will accept it or tell you what is missing.</li>
                        <li class="mb-2">If accepted, click <strong>Keep Permission</strong> to confirm.</li>
                        <li>If you no longer need this permission, click <strong>Remove Permission</strong> — no justification needed.</li>
                    </ol>
                    <p class="mb-0 mt-2" style="font-size:.78rem;color:#94a3b8;">Decisions save immediately and are final. Keeping a permission leaves it on your app; removing it takes it off right away. Nothing else is needed afterward. Return via this link later and only unreviewed permissions will appear.</p>
                </div>
                <div class="col-md-5 p-3" style="border-right:1px solid #d1d5db;">
                    <div class="fw-semibold mb-2" style="font-size:.75rem;text-transform:uppercase;letter-spacing:.05em;color:#64748b;">What to write</div>
                    <ul class="text-muted mb-3 ps-3">
                        <li class="mb-2"><strong>What your application does</strong> — one plain-English sentence (e.g. "This app reads emails from a shared mailbox and processes approval requests")</li>
                        <li class="mb-2"><strong>What this permission specifically enables</strong> — name the actual operation (e.g. "Mail.ReadWrite lets us move processed emails to an archive folder after reading them")</li>
                        <li class="mb-2"><strong>Why a weaker permission is not enough</strong> — if a narrower version of this permission exists, explain why it does not cover your case (e.g. "Mail.Read would not allow us to move or delete messages")</li>
                        <li><strong>What breaks without it</strong> — name the specific feature or workflow that would fail</li>
                    </ul>
                    <div style="background:#fff8f0;border-left:3px solid #f59e0b;padding:.55rem .75rem;border-radius:0 4px 4px 0;font-size:.8rem;color:#92400e;" class="mb-3">
                        <strong>Do not write:</strong> "Required for the application to function" or "Needed for business operations." These will always be rejected.
                    </div>
                    <div style="background:#f1f5f9;border-radius:4px;padding:.75rem;font-size:.8rem;color:#334155;font-style:italic;">
                        "This application monitors the shared procurement-approvals mailbox for requests submitted by our ERP system. It reads each message to extract the approval payload, then moves processed messages to the Archived folder. Mail.Read alone is insufficient — the workflow requires moving messages to a different folder after processing, which is a write operation."
                    </div>
                </div>
                <div class="col-md-4 p-3">
                    <div class="fw-semibold mb-2" style="font-size:.75rem;text-transform:uppercase;letter-spacing:.05em;color:#64748b;">When to escalate</div>
                    <p class="text-muted mb-2">Click <strong>Escalate for Human Review</strong> if any of these apply:</p>
                    <ul class="text-muted mb-3 ps-3">
                        <li class="mb-2">The AI keeps rejecting your justification and you are confident the permission is genuinely needed</li>
                        <li>You are unsure whether the permission is still actively used and need the IAM team to check usage data on your behalf</li>
                    </ul>
                    <p class="text-muted mb-2" style="font-size:.8rem;">After escalating, an IAM team member will review the permission and contact you directly. You do not need to take any further action.</p>
                    <div style="background:#fff8f0;border-left:3px solid #f59e0b;padding:.55rem .75rem;border-radius:0 4px 4px 0;font-size:.8rem;color:#92400e;">
                        Do not escalate to avoid writing a justification — a reviewer will ask you for one regardless.
                    </div>
                </div>
            </div>
            }
        </div>

        @if (allDone) {
        <div class="alert alert-success d-flex align-items-center gap-2 mb-4">
            <span>&#10003;</span>
            <span><strong>All done.</strong> Every permission has been reviewed. You can close this page.</span>
        </div>
        }

        @if (fullCyclePermissions.length > 0 && currentVm) {
        <div>

            <h5 class="text-center mb-2">Permission Review</h5>

            <!-- Dots + counter: progress indicator above the card -->
            <div class="d-flex align-items-center justify-content-center mb-3" style="gap:.6rem;">
                @for (vm of fullCyclePermissions; track vm.context.permissionId; let i = $index) {
                <button class="border-0 rounded-circle p-0"
                        style="width:12px;height:12px;cursor:pointer;transition:transform .15s;"
                        [style.background]="i === currentIndex ? '#4c6ef5' : vm.state === 'done' ? '#198754' : '#ced4da'"
                        [style.transform]="i === currentIndex ? 'scale(1.3)' : 'scale(1)'"
                        [title]="vm.context.permissionName"
                        (click)="slideDir = i > currentIndex ? 'next' : 'prev'; currentIndex = i">
                </button>
                }
                <span class="text-muted ms-1" style="font-size:.8rem;">{{ currentIndex + 1 }}&thinsp;/&thinsp;{{ fullCyclePermissions.length }}</span>
            </div>

            <!-- Card with colored header -->
            <div class="card mb-3" style="overflow:hidden;border:1px solid #dee2e6;box-shadow:0 2px 12px rgba(0,0,0,.07);"
                 [@cardSlide]="slideDir" (@cardSlide.done)="slideDir = null">

                <div class="text-start" style="background:#2d3748;">
                    <!-- Nav strip inside the dark header keeps it flush with the card edges -->
                    <div class="d-flex align-items-center px-4 pt-3 pb-2" style="border-bottom:1px solid rgba(255,255,255,.1);">
                        <div style="flex:1;">
                            <button style="background:transparent;color:rgba(255,255,255,.7);border:1px solid rgba(255,255,255,.3);border-radius:4px;font-size:.8rem;padding:.2rem .65rem;cursor:pointer;"
                                    [disabled]="currentIndex === 0"
                                    (click)="prevPermission()">&#8592; Previous</button>
                        </div>
                        <div style="flex:1;text-align:right;">
                            <button style="background:transparent;color:rgba(255,255,255,.7);border:1px solid rgba(255,255,255,.3);border-radius:4px;font-size:.8rem;padding:.2rem .65rem;cursor:pointer;"
                                    [disabled]="currentIndex === fullCyclePermissions.length - 1"
                                    (click)="nextPermission()">Next &#8594;</button>
                        </div>
                    </div>
                    <div class="p-4 pt-3">
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="flex-grow-1 me-3">
                                <div class="text-uppercase" style="font-size:.68rem;letter-spacing:.05em;color:rgba(255,255,255,.65);">{{ currentVm.context.resourceName }}</div>
                                <div class="fw-bold" style="font-size:1.15rem;color:#fff;">{{ currentVm.context.permissionName }}</div>
                                @if (currentVm.context.permissionDescription) {
                                <div style="font-size:.85rem;color:rgba(255,255,255,.72);margin-top:.4rem;line-height:1.5;">{{ currentVm.context.permissionDescription }}</div>
                                }
                                <div style="font-size:.75rem;color:rgba(255,255,255,.5);margin-top:.5rem;">
                                    @if (currentVm.context.permissionGrantedDate) {
                                    <span class="me-3">Granted {{ currentVm.context.permissionGrantedDate | date:'mediumDate' }}</span>
                                    }
                                    @if (currentVm.context.graphLastSignInDate) {
                                    <span>Last sign-in {{ currentVm.context.graphLastSignInDate | date:'mediumDate' }}</span>
                                    }
                                </div>
                            </div>
                            @if (currentVm.state === 'done') {
                            <span [class]="decisionBadgeClass(currentVm.finalDecision)">{{ currentVm.finalDecision }}</span>
                            }
                        </div>
                    </div>
                </div>

                <div class="p-4 text-start"
                     [style.background]="currentVm.state === 'evaluated_approved' || currentVm.state === 'done' ? '#f6fbf8' : currentVm.state === 'evaluated_rejected' ? '#fefaf8' : currentVm.state === 'escalating' ? '#f5f7ff' : '#fff'">

                    @if (currentVm.error) {
                    <div class="alert alert-danger py-2 mb-3" style="font-size:.875rem;">{{ currentVm.error }}</div>
                    }

                    @if (currentVm.state === 'idle') {
                    <!-- Renewal: previously attested, needs explicit re-confirmation -->
                    @if (!currentVm.context.isFullCycle) {
                    <div>
                        @if (currentVm.context.previousJustification) {
                        <div class="mb-3" style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:.85rem 1rem;font-size:.875rem;font-style:italic;color:#374151;line-height:1.6;">
                            "{{ currentVm.context.previousJustification }}"
                        </div>
                        }
                        <p class="mb-3" style="font-size:.875rem;color:#374151;">This is a Tier 0 permission — extraordinary risk to Chevron if misused. Review the justification above and confirm it still accurately reflects why this permission is genuinely needed today — scope and usage unchanged. If anything has changed, or if you have any doubt the permission is still actively required, remove it instead.</p>
                        @if (!currentVm.pendingAction) {
                        <div @fadeIn class="d-flex justify-content-center mt-4">
                            <button class="btn btn-success" style="margin-right:1rem;" (click)="currentVm.pendingAction = 'keep'">Confirm Still Needed</button>
                            <button class="btn btn-outline-danger" (click)="currentVm.pendingAction = 'remove'">Remove Permission</button>
                        </div>
                        }
                        @if (currentVm.pendingAction === 'keep') {
                        <div @fadeIn class="d-flex justify-content-center mt-4">
                            <div class="text-center px-4 py-3 rounded" style="background:#f0fdf4;border:1px solid #bbf7d0;">
                                <p class="fw-semibold mb-3" style="font-size:.875rem;color:#166534;">Are you sure you want to keep this permission?</p>
                                <div class="d-flex justify-content-center">
                                    <button class="btn btn-success btn-sm px-3" style="margin-right:1rem;" (click)="confirmRenewal(currentVm, true)">Yes, keep it</button>
                                    <button class="btn btn-outline-secondary btn-sm px-3" (click)="currentVm.pendingAction = undefined">No, go back</button>
                                </div>
                            </div>
                        </div>
                        }
                        @if (currentVm.pendingAction === 'remove') {
                        <div @fadeIn class="d-flex justify-content-center mt-4">
                            <div class="text-center px-4 py-3 rounded" style="background:#fef2f2;border:1px solid #fecaca;">
                                <p class="fw-semibold mb-3" style="font-size:.875rem;color:#991b1b;">Are you sure you want to remove this permission?</p>
                                <div class="d-flex justify-content-center">
                                    <button class="btn btn-danger btn-sm px-3" style="margin-right:1rem;" (click)="confirmRenewal(currentVm, false)">Yes, remove it</button>
                                    <button class="btn btn-outline-secondary btn-sm px-3" (click)="currentVm.pendingAction = undefined">No, go back</button>
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                    }
                    <!-- Full cycle: requires new justification + AI evaluation -->
                    @if (currentVm.context.isFullCycle) {
                    <div>
                        @if (currentVm.evalResult) {
                        <div class="mb-4">
                            <div class="d-flex align-items-center gap-2 mb-2" style="flex-wrap:nowrap;">
                                <span style="flex-shrink:0;white-space:nowrap;font-size:.75rem;font-weight:600;padding:.2rem .5rem;border-radius:.25rem;background:#fff0e6;color:#c0392b;border:1px solid #fdc78a;">Attempt {{ currentVm.attempts }} — not accepted</span>
                            </div>
                            @if (currentVm.evalResult.explanation) {
                            <p style="font-size:.875rem;color:#444;margin-bottom:.75rem;line-height:1.6;">{{ currentVm.evalResult.explanation }}</p>
                            }
                            @if ((currentVm.evalResult.clarificationQuestions?.length ?? 0) > 0) {
                            <p style="font-size:.75rem;font-weight:600;color:#888;text-transform:uppercase;letter-spacing:.04em;margin-bottom:.35rem;">Address in your revised justification</p>
                            <ol class="ps-4 mb-0" style="font-size:.875rem;color:#555;">
                                @for (q of (currentVm.evalResult.clarificationQuestions ?? []); track q) {
                                <li style="margin-bottom:.35rem;">{{ q }}</li>
                                }
                            </ol>
                            }
                        </div>
                        }
                        @if (currentVm.context.previousJustification && !currentVm.evalResult) {
                        <div class="text-muted fst-italic mb-3" style="font-size:.875rem;">Previous: "{{ currentVm.context.previousJustification }}"</div>
                        }
                        <label class="form-label fw-semibold mb-1" style="font-size:.875rem;">Business Justification</label>
                        <textarea class="form-control mb-3" rows="4"
                                  placeholder="Explain why this permission is necessary for legitimate business operations..."
                                  [(ngModel)]="currentVm.justification">
                        </textarea>
                        <button class="btn btn-primary"
                                [disabled]="!currentVm.justification.trim()"
                                (click)="evaluate(currentVm)">
                            Evaluate Justification
                        </button>
                        @if (!currentVm.pendingAction) {
                        <button class="btn btn-outline-danger ms-2" (click)="currentVm.pendingAction = 'remove'">Remove Permission</button>
                        }
                        @if (currentVm.pendingAction === 'remove') {
                        <div @fadeIn class="d-flex justify-content-center mt-4">
                            <div class="text-center px-4 py-3 rounded" style="background:#fef2f2;border:1px solid #fecaca;">
                                <p class="fw-semibold mb-3" style="font-size:.875rem;color:#991b1b;">Are you sure you want to remove this permission?</p>
                                <div class="d-flex justify-content-center">
                                    <button class="btn btn-danger btn-sm px-3" style="margin-right:1rem;" (click)="submitFullCycle(currentVm, false)">Yes, remove it</button>
                                    <button class="btn btn-outline-secondary btn-sm px-3" (click)="currentVm.pendingAction = undefined">No, go back</button>
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                    } <!-- end @if isFullCycle -->
                    } <!-- end @if state === 'idle' -->

                    @if (currentVm.state === 'evaluating') {
                    <div class="py-3 text-muted d-flex align-items-center gap-2" style="font-size:.875rem;">
                        <mat-spinner [diameter]="20"></mat-spinner>AI is evaluating your justification...
                    </div>
                    }

                    @if (currentVm.state === 'evaluated_approved') {
                    <div>
                        <div class="d-flex align-items-center gap-2 mb-3" style="flex-wrap:nowrap;">
                            <span style="flex-shrink:0;white-space:nowrap;font-size:.75rem;font-weight:600;padding:.2rem .5rem;border-radius:.25rem;background:#d3f9d8;color:#155724;border:1px solid #a3d9b1;">&#10003; Accepted</span>
                        </div>
                        @if (currentVm.evalResult?.explanation) {
                        <p style="font-size:.875rem;color:#444;margin-bottom:1rem;line-height:1.6;">{{ currentVm.evalResult?.explanation }}</p>
                        }
                        @if (submittedJustification(currentVm)) {
                        <div class="mb-3">
                            <div style="font-size:.75rem;font-weight:600;color:#888;text-transform:uppercase;letter-spacing:.04em;margin-bottom:.35rem;">Your justification</div>
                            <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:.75rem 1rem;font-size:.875rem;color:#374151;line-height:1.6;white-space:pre-wrap;">{{ submittedJustification(currentVm) }}</div>
                        </div>
                        }
                        <p class="fw-semibold mb-2" style="font-size:.875rem;">Confirm you want to keep this permission, or remove it if you've changed your mind.</p>
                        @if (!currentVm.pendingAction) {
                        <div class="d-flex gap-2 flex-wrap">
                            <button class="btn btn-success" (click)="currentVm.pendingAction = 'keep'">Keep Permission</button>
                            <button class="btn btn-outline-danger" (click)="currentVm.pendingAction = 'remove'">Remove Permission</button>
                        </div>
                        }
                        @if (currentVm.pendingAction === 'keep') {
                        <div @fadeIn class="d-flex justify-content-center mt-4">
                            <div class="text-center px-4 py-3 rounded" style="background:#f0fdf4;border:1px solid #bbf7d0;">
                                <p class="fw-semibold mb-3" style="font-size:.875rem;color:#166534;">Are you sure you want to keep this permission?</p>
                                <div class="d-flex justify-content-center">
                                    <button class="btn btn-success btn-sm px-3" style="margin-right:1rem;" (click)="submitFullCycle(currentVm, true)">Yes, keep it</button>
                                    <button class="btn btn-outline-secondary btn-sm px-3" (click)="currentVm.pendingAction = undefined">No, go back</button>
                                </div>
                            </div>
                        </div>
                        }
                        @if (currentVm.pendingAction === 'remove') {
                        <div @fadeIn class="d-flex justify-content-center mt-4">
                            <div class="text-center px-4 py-3 rounded" style="background:#fef2f2;border:1px solid #fecaca;">
                                <p class="fw-semibold mb-3" style="font-size:.875rem;color:#991b1b;">Are you sure you want to remove this permission?</p>
                                <div class="d-flex justify-content-center">
                                    <button class="btn btn-danger btn-sm px-3" style="margin-right:1rem;" (click)="submitFullCycle(currentVm, false)">Yes, remove it</button>
                                    <button class="btn btn-outline-secondary btn-sm px-3" (click)="currentVm.pendingAction = undefined">No, go back</button>
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                    }

                    @if (currentVm.state === 'evaluated_rejected') {
                    <div>
                        <div class="d-flex align-items-center gap-2 mb-2" style="flex-wrap:nowrap;">
                            <span style="flex-shrink:0;white-space:nowrap;font-size:.75rem;font-weight:600;padding:.2rem .5rem;border-radius:.25rem;background:#fff0e6;color:#c0392b;border:1px solid #fdc78a;">Not accepted</span>
                        </div>
                        @if (currentVm.evalResult?.explanation) {
                        <p style="font-size:.875rem;color:#444;margin-bottom:.75rem;line-height:1.6;">{{ currentVm.evalResult?.explanation }}</p>
                        }
                        @if ((currentVm.evalResult?.clarificationQuestions?.length ?? 0) > 0) {
                        <p style="font-size:.75rem;font-weight:600;color:#888;text-transform:uppercase;letter-spacing:.04em;margin-bottom:.35rem;">Address in your revised justification</p>
                        <ol class="ps-4 mb-3" style="font-size:.875rem;color:#555;">
                            @for (q of (currentVm.evalResult?.clarificationQuestions ?? []); track q) {
                            <li style="margin-bottom:.35rem;">{{ q }}</li>
                            }
                        </ol>
                        }
                        <div class="d-flex gap-2 align-items-center">
                            <button class="btn btn-primary" (click)="retryEvaluation(currentVm)">Try Again</button>
                            <button class="btn btn-outline-secondary" (click)="startEscalation(currentVm)">Escalate for Human Review</button>
                            @if (currentVm.attempts > 1) {
                            <span class="text-muted ms-1" style="font-size:.75rem;">{{ currentVm.attempts }} attempts</span>
                            }
                        </div>
                    </div>
                    }

                    @if (currentVm.state === 'evaluation_unavailable') {
                    <div>
                        <div class="rounded p-3 mb-3" style="background:#fff8f0;border:1px solid #fcd9a8;font-size:.875rem;color:#92400e;">
                            <strong>AI evaluation is temporarily unavailable.</strong> You attempted a review, so you may proceed — or try again once the service is back.
                        </div>
                        <p class="fw-semibold mb-2" style="font-size:.875rem;">Confirm you want to keep this permission, try evaluating again, or remove it.</p>
                        @if (!currentVm.pendingAction) {
                        <div class="d-flex gap-2 flex-wrap">
                            <button class="btn btn-primary" (click)="retryEvaluation(currentVm)">Try Evaluation Again</button>
                            <button class="btn btn-success" (click)="currentVm.pendingAction = 'keep'">Keep Permission</button>
                            <button class="btn btn-outline-danger" (click)="currentVm.pendingAction = 'remove'">Remove Permission</button>
                        </div>
                        }
                        @if (currentVm.pendingAction === 'keep') {
                        <div @fadeIn class="d-flex justify-content-center mt-4">
                            <div class="text-center px-4 py-3 rounded" style="background:#f0fdf4;border:1px solid #bbf7d0;">
                                <p class="fw-semibold mb-3" style="font-size:.875rem;color:#166534;">Are you sure you want to keep this permission?</p>
                                <div class="d-flex justify-content-center">
                                    <button class="btn btn-success btn-sm px-3" style="margin-right:1rem;" (click)="submitFullCycle(currentVm, true)">Yes, keep it</button>
                                    <button class="btn btn-outline-secondary btn-sm px-3" (click)="currentVm.pendingAction = undefined">No, go back</button>
                                </div>
                            </div>
                        </div>
                        }
                        @if (currentVm.pendingAction === 'remove') {
                        <div @fadeIn class="d-flex justify-content-center mt-4">
                            <div class="text-center px-4 py-3 rounded" style="background:#fef2f2;border:1px solid #fecaca;">
                                <p class="fw-semibold mb-3" style="font-size:.875rem;color:#991b1b;">Are you sure you want to remove this permission?</p>
                                <div class="d-flex justify-content-center">
                                    <button class="btn btn-danger btn-sm px-3" style="margin-right:1rem;" (click)="submitFullCycle(currentVm, false)">Yes, remove it</button>
                                    <button class="btn btn-outline-secondary btn-sm px-3" (click)="currentVm.pendingAction = undefined">No, go back</button>
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                    }

                    @if (currentVm.state === 'escalating') {
                    <div>
                        <div class="rounded p-3 mb-3" style="background:#f0f4ff;border:1px solid #c7d8f8;font-size:.875rem;">
                            Your request will be sent to the permission owner for manual review. Add any additional context to help them decide.
                        </div>
                        <label class="form-label fw-semibold mb-1" style="font-size:.875rem;">Additional context <span class="text-muted fw-normal">(optional)</span></label>
                        <textarea class="form-control mb-3" rows="3"
                                  placeholder="Any additional context for the reviewer..."
                                  [(ngModel)]="currentVm.escalationReason">
                        </textarea>
                        <div class="d-flex gap-2">
                            <button class="btn btn-secondary" (click)="submitEscalation(currentVm)">Submit for Review</button>
                            <button class="btn btn-link text-muted ps-0" (click)="cancelEscalation(currentVm)">Cancel</button>
                        </div>
                    </div>
                    }

                    @if (currentVm.state === 'submitting') {
                    <div class="py-3 text-muted d-flex align-items-center gap-2" style="font-size:.875rem;">
                        <mat-spinner [diameter]="20"></mat-spinner>Saving...
                    </div>
                    }

                    @if (currentVm.state === 'idle' || currentVm.state === 'evaluated_approved' || currentVm.state === 'evaluation_unavailable') {
                    <div class="mt-4 px-3 py-2" style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;font-size:.8rem;color:#475569;line-height:1.6;">
                        <strong>Your choice is final.</strong> Once you choose <strong>Keep Permission</strong> (or <strong>Confirm Still Needed</strong>) or <strong>Remove Permission</strong>, you are done with this permission — there is nothing else to submit.
                        A kept permission <strong>stays on {{ batchContext.servicePrincipalDisplayName }}</strong>. A removed permission is <strong>taken off {{ batchContext.servicePrincipalDisplayName }} right away</strong>.
                    </div>
                    }

                    @if (currentVm.state === 'done') {
                    @if (currentVm.finalDecision === 'Confirmed') {
                    <span class="text-muted" style="font-size:.875rem;">Saved. No further action is needed — this permission will stay on {{ batchContext.servicePrincipalDisplayName }}.</span>
                    } @else if (currentVm.finalDecision === 'Removed') {
                    <span class="text-muted" style="font-size:.875rem;">Saved. No further action is needed — this permission has been removed from {{ batchContext.servicePrincipalDisplayName }}.</span>
                    } @else {
                    <span class="text-muted" style="font-size:.875rem;">This permission has been reviewed.</span>
                    }
                    @if ((currentVm.finalDecision === 'Confirmed' || currentVm.finalDecision === 'Escalated') && submittedJustification(currentVm)) {
                    <div class="mt-3">
                        <div style="font-size:.75rem;font-weight:600;color:#888;text-transform:uppercase;letter-spacing:.04em;margin-bottom:.35rem;">Justification you submitted</div>
                        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:.75rem 1rem;font-size:.875rem;color:#374151;line-height:1.6;white-space:pre-wrap;">{{ submittedJustification(currentVm) }}</div>
                    </div>
                    }
                    }

                </div>
            </div>

        </div>
        }

        } <!-- end @if (!isLoading && !loadError && batchContext) -->

    </section>
</main>
